/**
 * Every tunable number in the volleyball sim lives here.
 *
 * World units are metres, +Y is up, origin is the bottom-left corner of the
 * court. The renderer is the only thing that knows about pixels.
 *
 * `TUNING` drives the debug panel: adding a constant here with a spec entry is
 * all that is needed for a live slider to appear.
 */

export interface Constants {
  // court
  courtWidth: number;
  courtHeight: number;
  netHeight: number;
  netThickness: number;

  // blob
  gravity: number;
  jumpImpulse: number;
  moveSpeed: number;
  airControl: number;
  blobRadius: number;
  blobAccel: number;

  // ball
  ballRadius: number;
  ballRestitution: number;
  ballFriction: number;
  ballLinearDamping: number;
  ballMaxSpeed: number;
  magnusCoefficient: number;
  spinTransfer: number;
  spinDamping: number;

  // blob <-> ball contact
  hitSpeed: number;
  hitRestitution: number;
  blobVelTransfer: number;

  // smash
  smashImpulse: number;
  smashAngleDeg: number;
  smashMinHeight: number;
  smashRange: number;
  smashCooldown: number;
  /** Bar refill rate, fraction per second. */
  energyRegen: number;
  /** Bar drain rate while smash is held, fraction per second. */
  energyDrain: number;

  // rules
  maxTouches: number;
  pointsToWin: number;
  winBy: number;
  serveDelay: number;
  pointDelay: number;
  /** Minimum seconds between two touches registered by the same blob. */
  touchLockout: number;
}

export const DEFAULTS: Constants = {
  courtWidth: 16,
  courtHeight: 9,
  netHeight: 3,
  netThickness: 0.14,

  gravity: -23.5,
  jumpImpulse: 13,
  moveSpeed: 8,
  airControl: 0.66,
  blobRadius: 0.9,
  blobAccel: 60,

  ballRadius: 0.34,
  ballRestitution: 0.9,
  ballFriction: 0.2,
  ballLinearDamping: 0.05,
  ballMaxSpeed: 38,
  magnusCoefficient: 0.06,
  spinTransfer: 0.35,
  spinDamping: 0.25,

  hitSpeed: 11,
  hitRestitution: 0.75,
  blobVelTransfer: 0.45,

  smashImpulse: 30,
  smashAngleDeg: 62,
  // Apex of a standing jump is jumpImpulse^2 / (2 * |gravity|) ~= 3.6 m, so
  // this threshold has to sit below that or the smash can never fire.
  smashMinHeight: 1.6,
  smashRange: 0.55,
  smashCooldown: 0.35,
  // A full bar is ~1.5 s of holding smash and refills from empty in ~6 s.
  energyRegen: 0.16,
  energyDrain: 0.65,

  maxTouches: 3,
  pointsToWin: 15,
  winBy: 2,
  serveDelay: 0.9,
  pointDelay: 1.3,
  touchLockout: 0.09,
};

export interface TuningSpec {
  key: keyof Constants;
  label: string;
  group: string;
  min: number;
  max: number;
  step: number;
}

export const TUNING: readonly TuningSpec[] = [
  { key: 'gravity', label: 'Gravity', group: 'Blob', min: -60, max: -2, step: 0.5 },
  { key: 'jumpImpulse', label: 'Jump impulse', group: 'Blob', min: 2, max: 20, step: 0.1 },
  { key: 'moveSpeed', label: 'Move speed', group: 'Blob', min: 1, max: 20, step: 0.1 },
  { key: 'airControl', label: 'Air control', group: 'Blob', min: 0, max: 1, step: 0.01 },
  { key: 'blobAccel', label: 'Ground accel', group: 'Blob', min: 5, max: 200, step: 1 },
  { key: 'blobRadius', label: 'Blob radius', group: 'Blob', min: 0.3, max: 2.5, step: 0.01 },

  { key: 'ballRadius', label: 'Ball radius', group: 'Ball', min: 0.1, max: 1.2, step: 0.01 },
  { key: 'ballRestitution', label: 'Restitution', group: 'Ball', min: 0, max: 1.1, step: 0.01 },
  { key: 'ballFriction', label: 'Friction', group: 'Ball', min: 0, max: 2, step: 0.01 },
  { key: 'ballLinearDamping', label: 'Linear damping', group: 'Ball', min: 0, max: 2, step: 0.01 },
  { key: 'ballMaxSpeed', label: 'Max speed', group: 'Ball', min: 5, max: 60, step: 0.5 },
  { key: 'magnusCoefficient', label: 'Magnus coeff', group: 'Ball', min: 0, max: 0.5, step: 0.005 },
  { key: 'spinTransfer', label: 'Spin transfer', group: 'Ball', min: 0, max: 2, step: 0.01 },
  { key: 'spinDamping', label: 'Spin damping', group: 'Ball', min: 0, max: 3, step: 0.01 },

  { key: 'hitSpeed', label: 'Hit speed', group: 'Contact', min: 2, max: 30, step: 0.1 },
  { key: 'hitRestitution', label: 'Hit restitution', group: 'Contact', min: 0, max: 1.5, step: 0.01 },
  { key: 'blobVelTransfer', label: 'Blob vel transfer', group: 'Contact', min: 0, max: 2, step: 0.01 },

  { key: 'smashImpulse', label: 'Smash impulse', group: 'Smash', min: 5, max: 50, step: 0.5 },
  { key: 'smashAngleDeg', label: 'Smash angle', group: 'Smash', min: 10, max: 89, step: 1 },
  { key: 'smashMinHeight', label: 'Smash min height', group: 'Smash', min: 0, max: 6, step: 0.05 },
  { key: 'smashRange', label: 'Smash range', group: 'Smash', min: 0, max: 3, step: 0.05 },
  { key: 'smashCooldown', label: 'Smash cooldown', group: 'Smash', min: 0, max: 2, step: 0.01 },
  { key: 'energyRegen', label: 'Energy regen /s', group: 'Smash', min: 0, max: 2, step: 0.01 },
  { key: 'energyDrain', label: 'Energy drain /s', group: 'Smash', min: 0, max: 5, step: 0.01 },

  { key: 'netHeight', label: 'Net height', group: 'Court', min: 0.5, max: 7, step: 0.05 },
  { key: 'netThickness', label: 'Net thickness', group: 'Court', min: 0.02, max: 1, step: 0.01 },
  { key: 'courtWidth', label: 'Court width', group: 'Court', min: 8, max: 30, step: 0.5 },
  { key: 'courtHeight', label: 'Court height', group: 'Court', min: 5, max: 20, step: 0.5 },

  { key: 'maxTouches', label: 'Touches per side', group: 'Rules', min: 1, max: 6, step: 1 },
  { key: 'pointsToWin', label: 'Points to win', group: 'Rules', min: 1, max: 25, step: 1 },
  { key: 'winBy', label: 'Win by', group: 'Rules', min: 1, max: 5, step: 1 },
  { key: 'serveDelay', label: 'Serve delay', group: 'Rules', min: 0, max: 3, step: 0.05 },
  { key: 'pointDelay', label: 'Point delay', group: 'Rules', min: 0, max: 4, step: 0.05 },
  { key: 'touchLockout', label: 'Touch lockout', group: 'Rules', min: 0, max: 0.5, step: 0.01 },
];

/** Fixed simulation timestep. Never step physics with a variable delta. */
export const SIM_HZ = 120;
export const SIM_DT = 1 / SIM_HZ;

export function cloneConstants(c: Constants): Constants {
  return { ...c };
}

/** Merge an untrusted JSON blob (pasted into the tuning panel) over defaults. */
export function constantsFromJSON(raw: unknown, base: Constants = DEFAULTS): Constants {
  const out = cloneConstants(base);
  if (typeof raw !== 'object' || raw === null) return out;
  for (const key of Object.keys(out) as (keyof Constants)[]) {
    const v = (raw as Record<string, unknown>)[key];
    if (typeof v === 'number' && Number.isFinite(v)) out[key] = v;
  }
  return out;
}
